import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Filter, X, Search, ChevronDown, ChevronUp, Calendar, Tag, ShieldCheck, UserCheck, Layers, Building, Globe, DollarSign, Award, Briefcase, Users, UserPlus } from 'lucide-react';
import {
  ApplicantFilterState,
  DEFAULT_FILTER_STATE,
  hasActiveFilters,
  getActiveFilterCount,
  buildMasterPhaseOptions,
  formatBatchOption,
  formatProgramOption,
  formatReferrerOption,
  formatSponsorOption,
  type FilterCriterionKey,
} from '../../lib/applicantFilterUtils';

interface UnifiedApplicantFilterBarProps {
  filters: ApplicantFilterState;
  onFilterChange: (filters: ApplicantFilterState) => void;
  /** Controls whether Filter By Master Phase is visible (ONLY true in Batches) */
  includeMasterPhase?: boolean;
  /**
   * Criteria this screen's record shape cannot feed. Hidden criteria
   * render no control or pills and do not count as active filters; the
   * screen is expected to also exclude them from its engine call (see
   * excludeFilterCriteria). Screens that pass nothing get every
   * criterion, exactly as before.
   */
  hiddenCriteria?: FilterCriterionKey[];
  /** Optional locked/focused program ID (e.g. when drilling into a Program Card) */
  lockedProgramId?: string;
  lockedProgramName?: string;
  /** Lookup lists for dynamic options */
  programsList?: any[];
  batchesList?: any[];
  sponsorsList?: any[];
  referrersList?: any[];
  teamMembersList?: any[];
  applications?: any[];
  className?: string;
}

const WORKFLOW_PHASE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'screening', label: 'Screening' },
  { value: 'processing', label: 'Processing & Admission' },
  { value: 'billings', label: 'Billings & Finance' },
  { value: 'immigration', label: 'Immigration & Visa' },
  { value: 'preDeparture', label: 'Pre-Departure' },
  { value: 'closed', label: 'Closed' },
];

const GENDER_OPTIONS = ['Male', 'Female', 'Non-Binary', 'Other', 'Prefer Not to Say'];

const FINANCIAL_STATION_OPTIONS = ['Outstanding Invoice', 'Full Paid', 'No Invoice', 'Partially Paid', 'Overdue', 'Waived'];

const APPLICATION_STATUS_OPTIONS = ['Draft', 'Submitted', 'Returned', 'Cancelled', 'Applicant Cancelled', 'Closed', 'Archived'];

// Exactly the getPlacementSummary().currentStatus vocabulary — an
// 'Unplaced' option could never match (unplaced records derive
// 'Pending Placement'), so it is not offered.
const PLACEMENT_STATUS_OPTIONS = ['Placed', 'Pending Approval', 'Pending Placement', 'Cancelled'];

export default function UnifiedApplicantFilterBar({
  filters,
  onFilterChange,
  includeMasterPhase = false,
  hiddenCriteria = [],
  lockedProgramId,
  lockedProgramName,
  programsList = [],
  batchesList = [],
  sponsorsList = [],
  referrersList = [],
  teamMembersList = [],
  applications = [],
  className = '',
}: UnifiedApplicantFilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const isHidden = (criterion: FilterCriterionKey) => hiddenCriteria.includes(criterion);

  // Master Phase options come from the programs' real pipeline
  // definitions (default pipeline + any custom program pipelines), so
  // every stage index a record can carry is selectable under its
  // actual stage name.
  const masterPhaseOptions = useMemo(
    () => buildMasterPhaseOptions(programsList),
    [programsList]
  );

  // Derived options from applications
  const derivedCountries = useMemo(() => {
    const set = new Set<string>();
    applications.forEach((a) => {
      const c = a.personalDetails?.countryOfCitizenship || a.personalDetails?.countryOfBirth || a.personalDetails?.country || a.country;
      if (c && typeof c === 'string') set.add(c.trim());
    });
    return Array.from(set).sort();
  }, [applications]);

  const derivedTags = useMemo(() => {
    const set = new Set<string>();
    applications.forEach((a) => {
      if (Array.isArray(a.tags)) {
        a.tags.forEach((t: string) => {
          if (t && typeof t === 'string') set.add(t.trim());
        });
      }
    });
    return Array.from(set).sort();
  }, [applications]);

  const derivedTerms = useMemo(() => {
    const set = new Set<string>();
    programsList.forEach((p) => {
      (p.terms || []).forEach((t: any) => {
        const label = typeof t === 'string' ? t : t?.label || t?.name || t?.termId;
        if (label) set.add(String(label).trim());
      });
    });
    applications.forEach((a) => {
      const t = a.programTerm || a.programPreferences?.programTerm || a.assignment?.programTerm;
      if (t) set.add(String(t).trim());
    });
    return Array.from(set).sort();
  }, [programsList, applications]);

  // Derived pricing variable options
  const derivedPricingVariables = useMemo(() => {
    const vars: { value: string; label: string }[] = [];
    const seen = new Set<string>();

    programsList.forEach((p) => {
      // If programs are selected in filters, only show variables for selected programs
      if (filters.program && filters.program.length > 0) {
        const match = filters.program.some((prg) => prg === p.id || prg === p.programId || prg === p.code || prg === p.name);
        if (!match) return;
      }
      (p.pricingVariables || []).forEach((v: any) => {
        if (v && v.id && !seen.has(v.id)) {
          seen.add(v.id);
          const codeStr = v.code ? ` (${v.code})` : '';
          vars.push({ value: v.id, label: `${p.name} - ${v.name}${codeStr}` });
        }
      });
    });

    applications.forEach((a) => {
      const vId = a.pricingVariableId || a.pricingVariableSnapshot?.pricingVariableId;
      const vName = a.pricingVariableName || a.pricingVariableSnapshot?.pricingVariableName;
      const vCode = a.pricingVariableCode || a.pricingVariableSnapshot?.pricingVariableCode;
      if (vId && !seen.has(vId)) {
        seen.add(vId);
        vars.push({ value: vId, label: `${vName || 'Variable'}${vCode ? ` (${vCode})` : ''}` });
      }
    });

    return vars;
  }, [programsList, applications, filters.program]);

  // Option lists with Reference Code FIRST
  const formattedProgramOptions = useMemo(() => {
    return programsList
      .filter((p) => p.active !== false)
      .map((p) => formatProgramOption(p));
  }, [programsList]);

  const formattedBatchOptions = useMemo(() => {
    return batchesList.map((b) => formatBatchOption(b));
  }, [batchesList]);

  const formattedReferrerOptions = useMemo(() => {
    if (referrersList.length > 0) {
      return referrersList.map((r) => formatReferrerOption(r));
    }
    // Fallback derived from applications
    const map = new Map<string, string>();
    applications.forEach((a) => {
      if (a.referrerId || a.referrerName) {
        const id = a.referrerId || a.referrerName;
        const name = a.referrerName || a.referrerId;
        map.set(id, name.startsWith('REF-') ? name : `${id} - ${name}`);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [referrersList, applications]);

  const formattedSponsorOptions = useMemo(() => {
    if (sponsorsList.length > 0) {
      return sponsorsList.map((s) => formatSponsorOption(s));
    }
    // Fallback derived from applications
    const map = new Map<string, string>();
    applications.forEach((a) => {
      const sp = a.assignedPartner || a.sponsorId || a.sponsorName || a.sponsor;
      if (sp) {
        map.set(sp, sp.startsWith('SPO-') ? sp : `SPO-${sp}`);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [sponsorsList, applications]);

  const formattedTeamMemberOptions = useMemo(() => {
    const list: { value: string; label: string }[] = [{ value: 'Unassigned', label: 'Unassigned' }];
    teamMembersList.forEach((u) => {
      const r = (u.workspaceRole || u.role || '').toLowerCase();
      if (r && !['applicant', 'job_seeker', 'candidate'].includes(r)) {
        const name = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unnamed Staff';
        const code = u.numericId || u.customId || u.uid;
        const codePrefix = code ? `#${code} ` : '';
        list.push({
          value: u.uid || u.id,
          label: `${codePrefix}${name} (${u.workspaceRole || u.role || 'Member'})`,
        });
      }
    });
    return list;
  }, [teamMembersList]);

  const activeCount = getActiveFilterCount(filters, { includeMasterPhase, hiddenCriteria });
  const isFiltered = hasActiveFilters(filters, { includeMasterPhase, hiddenCriteria });

  const toggleArrayItem = (key: keyof ApplicantFilterState, itemValue: string) => {
    const current = (filters[key] as string[]) || [];
    const updated = current.includes(itemValue)
      ? current.filter((v) => v !== itemValue)
      : [...current, itemValue];
    onFilterChange({ ...filters, [key]: updated });
  };

  const handleDateChange = (key: 'createdFrom' | 'createdTo' | 'submittedFrom' | 'submittedTo', value: string) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const clearAll = () => {
    onFilterChange({
      ...DEFAULT_FILTER_STATE,
      // Retain locked program if in drill-down view
      program: lockedProgramId ? [lockedProgramId] : [],
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Top Filter Bar Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50/80 border border-slate-200/80 rounded-2xl p-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-2 text-xs font-bold px-3.5 py-2 rounded-xl border transition-all cursor-pointer ${
              isOpen || activeCount > 0
                ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <Filter className="h-3.5 w-3.5" />
            <span>Advanced Filters</span>
            {activeCount > 0 && (
              <span className="bg-white text-blue-700 text-[11px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-xs">
                {activeCount}
              </span>
            )}
            {isOpen ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
          </button>

          {/* Active Filter Pills Summary */}
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Locked Focus View Badge */}
            {lockedProgramId && (
              <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 border border-amber-300 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                <Layers className="h-3 w-3 text-amber-700" />
                Focus View: {lockedProgramName || lockedProgramId}
              </span>
            )}

            {/* Workflow Phase Pills */}
            {!isHidden('workflowPhase') && filters.workflowPhase?.map((val) => {
              const label = WORKFLOW_PHASE_OPTIONS.find((o) => o.value === val)?.label || val;
              return (
                <span key={`phase-${val}`} className="inline-flex items-center gap-1 bg-blue-100 text-blue-900 border border-blue-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Phase: {label}
                  <button type="button" onClick={() => toggleArrayItem('workflowPhase', val)} className="text-blue-600 hover:text-blue-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {!isHidden('pricingVariable') && filters.pricingVariable?.map((val) => {
              const opt = derivedPricingVariables.find((o) => o.value === val);
              return (
                <span key={val} className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200">
                  <span className="text-[10px] text-indigo-500 uppercase font-black">Variable:</span>
                  <span>{opt?.label || val}</span>
                  <button type="button" onClick={() => toggleArrayItem('pricingVariable', val)} className="text-indigo-600 hover:text-indigo-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Sponsor Pills */}
            {!isHidden('sponsor') && filters.sponsor?.map((val) => {
              const label = formattedSponsorOptions.find((o) => o.value === val)?.label || val;
              return (
                <span key={`sponsor-${val}`} className="inline-flex items-center gap-1 bg-purple-100 text-purple-900 border border-purple-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Sponsor: {label}
                  <button type="button" onClick={() => toggleArrayItem('sponsor', val)} className="text-purple-600 hover:text-purple-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Gender Pills */}
            {!isHidden('gender') && filters.gender?.map((val) => (
              <span key={`gender-${val}`} className="inline-flex items-center gap-1 bg-pink-100 text-pink-900 border border-pink-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Gender: {val}
                <button type="button" onClick={() => toggleArrayItem('gender', val)} className="text-pink-600 hover:text-pink-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Country Pills */}
            {!isHidden('country') && filters.country?.map((val) => (
              <span key={`country-${val}`} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-900 border border-emerald-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Country: {val}
                <button type="button" onClick={() => toggleArrayItem('country', val)} className="text-emerald-600 hover:text-emerald-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Financial Station Pills */}
            {!isHidden('financialStation') && filters.financialStation?.map((val) => (
              <span key={`fin-${val}`} className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Finance: {val}
                <button type="button" onClick={() => toggleArrayItem('financialStation', val)} className="text-amber-600 hover:text-amber-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Status Pills */}
            {!isHidden('status') && filters.status?.map((val) => (
              <span key={`status-${val}`} className="inline-flex items-center gap-1 bg-sky-100 text-sky-900 border border-sky-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Status: {val}
                <button type="button" onClick={() => toggleArrayItem('status', val)} className="text-sky-600 hover:text-sky-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Master Phase Pills (ONLY in Batches) */}
            {includeMasterPhase && !isHidden('masterPhase') && filters.masterPhase?.map((val) => {
              const label = masterPhaseOptions.find((o) => o.value === val)?.label || `Phase ${val}`;
              return (
                <span key={`master-${val}`} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-900 border border-indigo-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Master: {label}
                  <button type="button" onClick={() => toggleArrayItem('masterPhase', val)} className="text-indigo-600 hover:text-indigo-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Tag Pills */}
            {!isHidden('tags') && filters.tags?.map((val) => (
              <span key={`tag-${val}`} className="inline-flex items-center gap-1 bg-teal-100 text-teal-900 border border-teal-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Tag: {val}
                <button type="button" onClick={() => toggleArrayItem('tags', val)} className="text-teal-600 hover:text-teal-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Placement Status Pills */}
            {!isHidden('placementStatus') && filters.placementStatus?.map((val) => (
              <span key={`place-${val}`} className="inline-flex items-center gap-1 bg-green-100 text-green-900 border border-green-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Placement: {val}
                <button type="button" onClick={() => toggleArrayItem('placementStatus', val)} className="text-green-600 hover:text-green-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Program Pills */}
            {!isHidden('program') && !lockedProgramId && filters.program?.map((val) => {
              const label = formattedProgramOptions.find((o) => o.value === val)?.label || val;
              return (
                <span key={`prg-${val}`} className="inline-flex items-center gap-1 bg-blue-100 text-blue-900 border border-blue-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Program: {label}
                  <button type="button" onClick={() => toggleArrayItem('program', val)} className="text-blue-600 hover:text-blue-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Term Pills */}
            {!isHidden('term') && filters.term?.map((val) => (
              <span key={`term-${val}`} className="inline-flex items-center gap-1 bg-cyan-100 text-cyan-900 border border-cyan-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Term: {val}
                <button type="button" onClick={() => toggleArrayItem('term', val)} className="text-cyan-600 hover:text-cyan-900 cursor-pointer ml-0.5">&times;</button>
              </span>
            ))}

            {/* Batch Pills */}
            {!isHidden('batches') && filters.batches?.map((val) => {
              const label = formattedBatchOptions.find((o) => o.value === val)?.label || val;
              return (
                <span key={`batch-${val}`} className="inline-flex items-center gap-1 bg-orange-100 text-orange-900 border border-orange-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Batch: {label}
                  <button type="button" onClick={() => toggleArrayItem('batches', val)} className="text-orange-600 hover:text-orange-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Referrer Pills */}
            {!isHidden('referrer') && filters.referrer?.map((val) => {
              const label = formattedReferrerOptions.find((o) => o.value === val)?.label || val;
              return (
                <span key={`ref-${val}`} className="inline-flex items-center gap-1 bg-violet-100 text-violet-900 border border-violet-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Referrer: {label}
                  <button type="button" onClick={() => toggleArrayItem('referrer', val)} className="text-violet-600 hover:text-violet-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Team Member Pills */}
            {!isHidden('assignedTeamMember') && filters.assignedTeamMember?.map((val) => {
              const label = formattedTeamMemberOptions.find((o) => o.value === val)?.label || val;
              return (
                <span key={`team-${val}`} className="inline-flex items-center gap-1 bg-slate-200 text-slate-900 border border-slate-300 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                  Staff: {label}
                  <button type="button" onClick={() => toggleArrayItem('assignedTeamMember', val)} className="text-slate-600 hover:text-slate-900 cursor-pointer ml-0.5">&times;</button>
                </span>
              );
            })}

            {/* Date Range Badges */}
            {!isHidden('createdRange') && (filters.createdFrom || filters.createdTo) && (
              <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Created: {filters.createdFrom || '—'} to {filters.createdTo || '—'}
                <button type="button" onClick={() => onFilterChange({ ...filters, createdFrom: '', createdTo: '' })} className="text-slate-500 hover:text-slate-800 cursor-pointer ml-0.5">&times;</button>
              </span>
            )}
            {!isHidden('submittedRange') && (filters.submittedFrom || filters.submittedTo) && (
              <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-2xs">
                Submitted: {filters.submittedFrom || '—'} to {filters.submittedTo || '—'}
                <button type="button" onClick={() => onFilterChange({ ...filters, submittedFrom: '', submittedTo: '' })} className="text-slate-500 hover:text-slate-800 cursor-pointer ml-0.5">&times;</button>
              </span>
            )}
          </div>
        </div>

        {isFiltered && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer px-2 py-1"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Expanded Filter Selection Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-md space-y-5 animate-slide-down"
        >
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Filter className="h-4 w-4 text-blue-600" />
              Comprehensive Filter Criteria
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-xs font-semibold text-slate-700">
            {/* 1. Workflow Phase MultiSelect */}
            {!isHidden('workflowPhase') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-blue-600" />
                Workflow Phase
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('workflowPhase', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-blue-500 cursor-pointer"
              >
                <option value="">{filters.workflowPhase?.length ? `+ Add Phase (${filters.workflowPhase.length})` : 'All Workflow Phases'}</option>
                {WORKFLOW_PHASE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.workflowPhase?.includes(o.value)}>
                    {o.label} {filters.workflowPhase?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 2. Sponsor / Partner Assigned MultiSelect */}
            {!isHidden('sponsor') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Building className="h-3.5 w-3.5 text-purple-600" />
                Sponsor / Partner Assigned
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('sponsor', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-purple-500 cursor-pointer"
              >
                <option value="">{filters.sponsor?.length ? `+ Add Sponsor (${filters.sponsor.length})` : 'All Sponsors / Partners'}</option>
                {formattedSponsorOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.sponsor?.includes(o.value)}>
                    {o.label} {filters.sponsor?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 3. Gender MultiSelect */}
            {!isHidden('gender') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-pink-600" />
                Gender
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('gender', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-pink-500 cursor-pointer"
              >
                <option value="">{filters.gender?.length ? `+ Add Gender (${filters.gender.length})` : 'All Genders'}</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g} value={g} disabled={filters.gender?.includes(g)}>
                    {g} {filters.gender?.includes(g) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 4. Country MultiSelect */}
            {!isHidden('country') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-emerald-600" />
                Country
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('country', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-emerald-500 cursor-pointer"
              >
                <option value="">{filters.country?.length ? `+ Add Country (${filters.country.length})` : 'All Countries'}</option>
                {derivedCountries.map((c) => (
                  <option key={c} value={c} disabled={filters.country?.includes(c)}>
                    {c} {filters.country?.includes(c) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 5. Financial Station MultiSelect */}
            {!isHidden('financialStation') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-amber-600" />
                Financial Station
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('financialStation', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-amber-500 cursor-pointer"
              >
                <option value="">{filters.financialStation?.length ? `+ Add Financial Status (${filters.financialStation.length})` : 'All Financial Stations'}</option>
                {FINANCIAL_STATION_OPTIONS.map((fs) => (
                  <option key={fs} value={fs} disabled={filters.financialStation?.includes(fs)}>
                    {fs} {filters.financialStation?.includes(fs) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 6. Application Status MultiSelect */}
            {!isHidden('status') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-sky-600" />
                Application Status
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('status', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-sky-500 cursor-pointer"
              >
                <option value="">{filters.status?.length ? `+ Add Status (${filters.status.length})` : 'All Application Statuses'}</option>
                {APPLICATION_STATUS_OPTIONS.map((st) => (
                  <option key={st} value={st} disabled={filters.status?.includes(st)}>
                    {st} {filters.status?.includes(st) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 7. Master Phase MultiSelect (ONLY in Batches!) */}
            {includeMasterPhase && !isHidden('masterPhase') && (
              <div className="space-y-1.5">
                <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5 text-indigo-600" />
                  Master Phase (Batches Only)
                </label>
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) toggleArrayItem('masterPhase', e.target.value); }}
                  className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-indigo-500 cursor-pointer"
                >
                  <option value="">{filters.masterPhase?.length ? `+ Add Master Phase (${filters.masterPhase.length})` : 'All Master Phases'}</option>
                  {masterPhaseOptions.map((mp) => (
                    <option key={mp.value} value={mp.value} disabled={filters.masterPhase?.includes(mp.value)}>
                      {mp.label} {filters.masterPhase?.includes(mp.value) ? '✓' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 8. Application Tags MultiSelect */}
            {!isHidden('tags') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-teal-600" />
                Application Tags
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('tags', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-teal-500 cursor-pointer"
              >
                <option value="">{filters.tags?.length ? `+ Add Tag (${filters.tags.length})` : 'All Application Tags'}</option>
                {derivedTags.map((t) => (
                  <option key={t} value={t} disabled={filters.tags?.includes(t)}>
                    {t} {filters.tags?.includes(t) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 9. Placement / Placed Status MultiSelect */}
            {!isHidden('placementStatus') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5 text-green-600" />
                Placement / Placed Status
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('placementStatus', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-green-500 cursor-pointer"
              >
                <option value="">{filters.placementStatus?.length ? `+ Add Placement Status (${filters.placementStatus.length})` : 'All Placement Statuses'}</option>
                {PLACEMENT_STATUS_OPTIONS.map((ps) => (
                  <option key={ps} value={ps} disabled={filters.placementStatus?.includes(ps)}>
                    {ps} {filters.placementStatus?.includes(ps) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 12. Program MultiSelect */}
            {!isHidden('program') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-blue-600" />
                Program (Ref First)
              </label>
              {lockedProgramId ? (
                <div className="w-full px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs font-bold text-amber-900 truncate">
                  {lockedProgramName || lockedProgramId} (Locked)
                </div>
              ) : (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) toggleArrayItem('program', e.target.value); }}
                  className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-blue-500 cursor-pointer"
                >
                  <option value="">{filters.program?.length ? `+ Add Program (${filters.program.length})` : 'All Programs'}</option>
                  {formattedProgramOptions.map((o) => (
                    <option key={o.value} value={o.value} disabled={filters.program?.includes(o.value)}>
                      {o.label} {filters.program?.includes(o.value) ? '✓' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            )}

            {/* 12b. Program Variable MultiSelect */}
            {!isHidden('pricingVariable') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-indigo-600" />
                Program Variable
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('pricingVariable', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-indigo-500 cursor-pointer"
              >
                <option value="">{filters.pricingVariable?.length ? `+ Add Variable (${filters.pricingVariable.length})` : 'All Variables'}</option>
                {derivedPricingVariables.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.pricingVariable?.includes(o.value)}>
                    {o.label} {filters.pricingVariable?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 13. Term MultiSelect */}
            {!isHidden('term') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-cyan-600" />
                Term
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('term', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-cyan-500 cursor-pointer"
              >
                <option value="">{filters.term?.length ? `+ Add Term (${filters.term.length})` : 'All Program Terms'}</option>
                {derivedTerms.map((t) => (
                  <option key={t} value={t} disabled={filters.term?.includes(t)}>
                    {t} {filters.term?.includes(t) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 14. Batches MultiSelect (Ref Code First Always) */}
            {!isHidden('batches') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-orange-600" />
                Batches (Ref First)
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('batches', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-orange-500 cursor-pointer"
              >
                <option value="">{filters.batches?.length ? `+ Add Batch (${filters.batches.length})` : 'All Batches'}</option>
                {formattedBatchOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.batches?.includes(o.value)}>
                    {o.label} {filters.batches?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 15. Referrer Assigned MultiSelect (Ref First) */}
            {!isHidden('referrer') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5 text-violet-600" />
                Referrer Assigned (Ref First)
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('referrer', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-violet-500 cursor-pointer"
              >
                <option value="">{filters.referrer?.length ? `+ Add Referrer (${filters.referrer.length})` : 'All Referrers'}</option>
                {formattedReferrerOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.referrer?.includes(o.value)}>
                    {o.label} {filters.referrer?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 16. Assigned Team Member MultiSelect */}
            {!isHidden('assignedTeamMember') && (
            <div className="space-y-1.5">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 text-slate-600" />
                Assigned Team Member
              </label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleArrayItem('assignedTeamMember', e.target.value); }}
                className="w-full text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white focus:border-slate-500 cursor-pointer"
              >
                <option value="">{filters.assignedTeamMember?.length ? `+ Add Staff (${filters.assignedTeamMember.length})` : 'All Staff Members'}</option>
                {formattedTeamMemberOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={filters.assignedTeamMember?.includes(o.value)}>
                    {o.label} {filters.assignedTeamMember?.includes(o.value) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* 10. Creation Date Range */}
            {!isHidden('createdRange') && (
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-blue-600" />
                Application Created Date Range (From / To)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={filters.createdFrom || ''}
                  onChange={(e) => handleDateChange('createdFrom', e.target.value)}
                  className="w-full text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white"
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={filters.createdTo || ''}
                  onChange={(e) => handleDateChange('createdTo', e.target.value)}
                  className="w-full text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white"
                  placeholder="To Date"
                />
              </div>
            </div>
            )}

            {/* 11. Submitted Date Range */}
            {!isHidden('submittedRange') && (
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-caption uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-indigo-600" />
                Application Submitted Date Range (From / To)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={filters.submittedFrom || ''}
                  onChange={(e) => handleDateChange('submittedFrom', e.target.value)}
                  className="w-full text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white"
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={filters.submittedTo || ''}
                  onChange={(e) => handleDateChange('submittedTo', e.target.value)}
                  className="w-full text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 outline-none bg-slate-50 focus:bg-white"
                  placeholder="To Date"
                />
              </div>
            </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={clearAll}
              className="px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all cursor-pointer"
            >
              Reset All Filters
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
            >
              Apply Filters ({activeCount})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
